/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/config/featureFlags.ts
 * PURPOSE: Configuration and environment settings
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: Frontend Application, Vietnamese F&B Platform
 * - Impact: Application Quality, User Experience, Performance
 * - Stakeholders: Development Team, End Users, Product Team
 *
 * @technical-architecture
 * - Layer: Frontend Application Layer
 * - Pattern: React Application, TypeScript, Modern Frontend
 * - Dependencies: React 18, TypeScript, Vite, TailwindCSS
 */

type BoolLike = string | boolean | undefined | null;

// Exported for unit testing and reuse
export const parseBoolean = (value: BoolLike, defaultValue = false): boolean => {
  if (value === undefined || value === null) return defaultValue;
  if (typeof value === 'boolean') return value;
  const s = String(value).trim().toLowerCase();
  return ['1', 'true', 'yes', 'on', 'y'].includes(s) ? true : ['0', 'false', 'no', 'off', 'n'].includes(s) ? false : defaultValue;
};

export interface FeatureFlags {
  attendanceModern: boolean;
  scheduleModern: boolean;
  analyticsModern: boolean;
  /** Chatbot + Bot Config UI. Hidden in the pilot (CEO 04/10 via @ceo, #1217). */
  chatbot: boolean;
}

// Factory to create flags from a provided env-like object (testable)
export const makeFeatureFlags = (env: Record<string, BoolLike>): FeatureFlags => ({
  attendanceModern: parseBoolean(env.VITE_FEATURE_ATTENDANCE_MODERN, false),
  scheduleModern: parseBoolean(env.VITE_FEATURE_SCHEDULE_MODERN, false),
  analyticsModern: parseBoolean(env.VITE_FEATURE_ANALYTICS_MODERN, false),
  chatbot: parseBoolean(env.VITE_FEATURE_CHATBOT, false),
});

export const featureFlags: FeatureFlags = makeFeatureFlags(import.meta.env as unknown as Record<string, BoolLike>);

// Generic selector helper (useful in routing and components)
export const pickBy = <T,>(enabled: boolean, modern: T, legacy: T): T => (enabled ? modern : legacy);

// Convenience helpers
export const isAttendanceModernEnabled = () => featureFlags.attendanceModern;
export const isScheduleModernEnabled = () => featureFlags.scheduleModern;
export const isAnalyticsModernEnabled = () => featureFlags.analyticsModern;
export const isChatbotEnabled = () => featureFlags.chatbot;

// Example (do not import non-existent components; bind when implemented):
// const AttendanceRoute = isAttendanceModernEnabled() ? AttendanceModern : MagicClock;
// <Route path="/attendance" element={<AttendanceRoute />} />
